import React, { useState, useMemo, useEffect, Suspense } from 'react';
import {
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
  Divider,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Alert,
  useTheme,
  Skeleton,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { chartTooltipProps } from '@/lib/chartTheme';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { AttachmentsCard } from '@/components/attachments/AttachmentsCard';
import type { EntityRef } from '@/hooks/useAttachments';
import { VehicleMakeLogo, RegistrationPlate } from '@/components/common';
import PersonIcon from '@mui/icons-material/Person';
import SpeedIcon from '@mui/icons-material/Speed';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import HistoryIcon from '@mui/icons-material/History';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StraightenIcon from '@mui/icons-material/Straighten';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import InfoIcon from '@mui/icons-material/Info';
import UndoIcon from '@mui/icons-material/Undo';
import { format, differenceInDays, isPast, formatDistanceToNow, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotify } from '@/hooks/useNotify';
import { useVehicleLookup, type VehicleLookupResult } from '@/hooks/useVehicleLookup';
import { formatFinnishPlate } from '@/utils/finnishPlate';
import { useUpdateVehicle, useLatestMileage, useMileageHistory, useVehicle } from '@/hooks/useVehicles';
import type { VehicleDetail, VehicleListItem } from '@/hooks/useVehicles';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/queryKeys';
import { ApiError } from '@/lib/api';
import { isTemporarilyUnavailable } from '@/lib/describeApiError';
import { notifyVehicleSaveError } from './vehicleSaveError';
import { useAuthStore } from '@/stores/authStore';
import type { VehicleDetails, ServiceHistoryEntry, TraficomData } from '@/types';
import { InlineVehicleFields } from './InlineVehicleFields';
import { VehicleServiceBookLinks } from './VehicleServiceBookLinks';
import { VehicleTechnicalDataPanel } from './technical-data';
import { getInspectionStatusKind } from '@/components/workorders/create/VehicleInspectionSummary';
import { dateFnsLocale, formatNumber } from '@/utils/locale';
import {
  VEHICLE_LOOKUP_FIELDS,
  VehicleLookupDiffDialog,
  applyLookupToOverlay,
  buildVehicleLookupDiff,
  displayedOverlayValue,
  nonBlankVin,
  overlayHasKey,
  parseOverlayInput,
  validateOverlayInput,
  type VehicleLookupDiffItem,
  type VehicleLookupFieldId,
  type VehicleLookupFieldSpec,
} from './VehicleFormFields';

const WorkOrderCreateModal = React.lazy(() =>
  import('@/components/workorders/WorkOrderCreateModal').then((m) => ({
    default: m.WorkOrderCreateModal,
  })),
);

interface VehicleDetailModalProps {
  vehicle: VehicleDetails | null;
  serviceHistory: ServiceHistoryEntry[];
  open: boolean;
  onClose: () => void;
  /** Hide the footer action that navigates away (nested in the create wizard). */
  hideCreateWorkOrder?: boolean;
}

function getLinkDisplayName(link: {
  customerType: string;
  businessName: string | null;
  customerFirstName: string;
  customerLastName: string;
}): string {
  if (link.customerType === 'business' && link.businessName) {
    return link.businessName;
  }
  return `${link.customerFirstName} ${link.customerLastName}`.trim();
}

type VehiclePartyLink = {
  customerId: string;
  customerType: string;
  businessName: string | null;
  customerFirstName: string;
  customerLastName: string;
  customerPhone: string | null;
  partyType?: 'person' | 'company';
  personId?: string | null;
  companyId?: string | null;
};

function linkedPartyFromVehicle(
  vehicle: VehicleDetails,
  liveDetail: VehicleDetail | undefined,
): VehiclePartyLink | null {
  return liveDetail?.owner ?? liveDetail?.holder ?? liveDetail?.workOrderers[0]
    ?? vehicle.owner ?? vehicle.holder ?? vehicle.workOrderers?.[0]
    ?? null;
}

/** Map the open vehicle card onto the create-wizard's existing-vehicle slot (#2899). */
function toCreateWizardVehicle(
  vehicle: VehicleDetails,
  liveDetail: VehicleDetail | undefined,
): VehicleListItem {
  const link = linkedPartyFromVehicle(vehicle, liveDetail);
  const item: VehicleListItem = {
    id: vehicle.id,
    customerId: link?.customerId ?? vehicle.customerId,
    registrationNumber: vehicle.registrationNumber,
    make: liveDetail?.make ?? vehicle.make,
    model: liveDetail?.model ?? vehicle.model,
    year: liveDetail?.year ?? vehicle.year,
    vin: nonBlankVin(liveDetail?.vin) ?? nonBlankVin(vehicle.vin),
    mileage: liveDetail?.mileage ?? vehicle.mileage ?? null,
    lastServiceDate: liveDetail?.lastServiceDate ?? vehicle.lastServiceAt ?? null,
    createdAt: liveDetail?.createdAt ?? vehicle.createdAt,
    updatedAt: liveDetail?.updatedAt ?? vehicle.createdAt,
    customerName: link
      ? getLinkDisplayName(link)
      : `${vehicle.customer.firstName} ${vehicle.customer.lastName}`.trim(),
    customerPhone: link?.customerPhone ?? vehicle.customer.phone ?? '',
  };
  if (link?.partyType) {
    item.partyType = link.partyType;
    item.personId = link.personId ?? null;
    item.companyId = link.companyId ?? null;
  }
  const notes = liveDetail?.notes ?? vehicle.notes;
  if (notes) item.notes = notes;
  return item;
}

const engineTypeKeys: Record<string, string> = {
  petrol: 'engineTypes.petrol',
  diesel: 'engineTypes.diesel',
  hybrid: 'engineTypes.hybrid',
  electric: 'engineTypes.electric',
};

interface DataRowProps {
  label: string;
  value: string | number | null | undefined;
  unit?: string;
}

function DataRow({ label, value, unit }: DataRowProps) {
  if (value === null || value === undefined) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}{unit ? ` ${unit}` : ''}
      </Typography>
    </Box>
  );
}

function toOverlayInput(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    return value;
  }
  return String(value);
}

function OverlayField({
  spec,
  label,
  value,
  canUpdate,
  isOverride,
  onSave,
  onRestore,
  formatDisplay,
}: {
  spec: VehicleLookupFieldSpec;
  label: string;
  value: unknown;
  canUpdate: boolean;
  isOverride: boolean;
  onSave: (parsed: unknown) => void | Promise<void>;
  onRestore: () => void;
  formatDisplay?: (value: unknown) => string;
}) {
  const { t } = useTranslation('vehicles');
  const [draft, setDraft] = useState(() => toOverlayInput(value));
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    setDraft(toOverlayInput(value));
    setError(undefined);
  }, [value]);

  if (!canUpdate) {
    if (value == null || value === '') return null;
    const display = formatDisplay ? formatDisplay(value) : toOverlayInput(value);
    return <DataRow label={label} value={display} {...(spec.unit !== undefined ? { unit: spec.unit } : {})} />;
  }

  const commit = () => {
    const message = validateOverlayInput(spec, draft, t);
    if (message) {
      setError(message);
      return;
    }
    setError(undefined);
    const parsed = parseOverlayInput(spec, draft);
    if (toOverlayInput(parsed) === toOverlayInput(value) || (parsed == null && (value == null || value === ''))) {
      return;
    }
    const committedDraft = draft;
    void Promise.resolve(onSave(parsed)).catch(() => {
      setDraft((current) => (current === committedDraft ? toOverlayInput(value) : current));
    });
  };

  const helper = error
    ?? (isOverride ? t('source.manual') : value != null && value !== '' ? t('source.registry') : undefined);

  return (
    <Box sx={{ py: 0.5 }}>
      <TextField
        label={label}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          if (error) setError(undefined);
        }}
        onBlur={commit}
        error={!!error}
        helperText={helper}
        fullWidth
        size="small"
        type={spec.kind === 'int' ? 'number' : spec.kind === 'date' ? 'date' : 'text'}
        slotProps={{
          ...(spec.kind === 'date' ? { inputLabel: { shrink: true } } : {}),
          input: {
            endAdornment: (
              <InputAdornment position="end">
                {spec.unit ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mr: isOverride ? 0 : 0.5 }}>
                    {spec.unit}
                  </Typography>
                ) : null}
                {isOverride && (
                  <Tooltip title={t('source.restore')}>
                    <IconButton
                      size="small"
                      onClick={onRestore}
                      aria-label={t('source.restore')}
                      edge="end"
                    >
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
    </Box>
  );
}

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {icon}
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
    </Box>
  );
}

export function VehicleDetailModal({
  vehicle,
  serviceHistory,
  open,
  onClose,
  hideCreateWorkOrder = false,
}: VehicleDetailModalProps) {
  const { t } = useTranslation('vehicles');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const lookupMutation = useVehicleLookup();
  const updateMutation = useUpdateVehicle();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canUpdate = hasPermission('vehicles.update');
  const [activeTab, setActiveTab] = useState(0);
  // History and attachments both lead to a work order: close the card first.
  const openWorkOrder = (bookingId: string) => {
    onClose();
    navigate(`/workorders/${bookingId}`);
  };
  const [traficomRefreshing, setTraficomRefreshing] = useState(false);
  const [lookupDiff, setLookupDiff] = useState<{
    items: VehicleLookupDiffItem[];
    result: VehicleLookupResult;
  } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createVehicle, setCreateVehicle] = useState<VehicleListItem | null>(null);

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setCreateVehicle(null);
  };

  const createWizard = (
    <Suspense fallback={null}>
      <WorkOrderCreateModal
        open={createOpen}
        onClose={handleCloseCreate}
        mechanics={[]}
        {...(createVehicle ? { initialVehicle: createVehicle } : {})}
      />
    </Suspense>
  );

  const { data: latestMileage } = useLatestMileage(vehicle?.id);
  const { data: mileageHistoryData, isLoading: mileageHistoryLoading } = useMileageHistory(vehicle?.id);
  const { data: liveDetail } = useVehicle(vehicle?.id);

  // Attachments tab (#1043): show the vehicle's own files plus files from its
  // work orders. Fetch by [vehicle, ...its bookings] — service-history entry ids
  // are the booking ids — so booking-only legacy attachments surface too. New
  // uploads link to the vehicle only; work-order files stay view-only here.
  const attachmentEntityLinks = useMemo<EntityRef[]>(() => {
    if (!vehicle?.id) return [];
    return [
      { entityType: 'vehicle', entityId: vehicle.id },
      ...serviceHistory.map((entry) => ({ entityType: 'booking' as const, entityId: entry.id })),
    ];
  }, [vehicle?.id, serviceHistory]);
  const vehicleUploadLinks = useMemo<EntityRef[]>(
    () => (vehicle?.id ? [{ entityType: 'vehicle', entityId: vehicle.id }] : []),
    [vehicle?.id],
  );

  const mileageChartData = useMemo(() => {
    if (!mileageHistoryData?.data) return [];
    return mileageHistoryData.data
      .filter((entry) => entry.recordedAt)
      .map((entry) => ({
        date: format(new Date(entry.recordedAt!), 'd.M.yyyy', { locale: dateFnsLocale() }),
        mileage: entry.mileage,
        source: entry.source,
      }));
  }, [mileageHistoryData]);

  const getSourceLabel = (source: string | null) => {
    if (!source) return '';
    const key = `mileageSource.${source}`;
    const translated = t(key);
    return translated === key ? source : translated;
  };

  if (!vehicle) return createOpen ? createWizard : null;

  const data = (liveDetail?.traficomData ?? vehicle.traficomData) as TraficomData | null | undefined;
  const overrides = (liveDetail?.manualOverrides ?? vehicle.manualOverrides ?? null) as
    | Record<string, unknown>
    | null;
  const traficomRecord = (data ?? {}) as Record<string, unknown>;

  const overlayValue = (overlayKey: string, fallback?: unknown): unknown =>
    displayedOverlayValue(overlayKey, overrides, data as Record<string, unknown> | null | undefined, fallback);

  const lookupFieldById = (id: VehicleLookupFieldId): VehicleLookupFieldSpec | undefined =>
    VEHICLE_LOOKUP_FIELDS.find((field) => field.id === id);

  const formatPrice = (cents: number) => `€${(Number(cents) / 100).toFixed(2)}`;

  const getExpiryStatus = (date?: string) => {
    if (!date) return null;
    const expiryDate = new Date(date);
    const daysUntil = differenceInDays(expiryDate, new Date());

    if (isPast(expiryDate)) {
      return { color: 'error' as const, label: t('expired') };
    }
    if (daysUntil <= 30) {
      return { color: 'warning' as const, label: `${daysUntil} ${t('daysLeft')}` };
    }
    return { color: 'success' as const, label: format(expiryDate, 'd.M.yyyy') };
  };

  const getInspectionStatus = () => {
    const status = getInspectionStatusKind(data?.nextInspectionDue);
    if (!status) return null;
    if (status.kind === 'overdue') {
      return { color: 'error' as const, icon: <ErrorIcon />, label: t('traficom.inspectionOverdue') };
    }
    if (status.kind === 'expiring') {
      return { color: 'warning' as const, icon: <WarningIcon />, label: `${String(status.daysUntil)} ${t('traficom.daysLeft')}` };
    }
    return { color: 'success' as const, icon: <VerifiedIcon />, label: t('traficom.inspectionValid') };
  };

  const liveNotes = liveDetail?.notes ?? vehicle.notes ?? null;

  const getInspectionResultChip = () => {
    if (!data?.inspectionResult) return null;
    const configs = {
      approved: { color: 'success' as const, label: t('traficom.approved') },
      approved_with_notes: { color: 'warning' as const, label: t('traficom.approvedWithNotes') },
      rejected: { color: 'error' as const, label: t('traficom.rejected') },
    };
    const config = configs[data.inspectionResult];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const knownColumnVin = nonBlankVin(liveDetail?.vin) ?? nonBlankVin(vehicle.vin);

  const currentDisplayByOverlayKey = (): Record<string, unknown> => {
    const current: Record<string, unknown> = {};
    for (const spec of VEHICLE_LOOKUP_FIELDS) {
      const fallback =
        spec.id === 'make' ? vehicle.make
        : spec.id === 'model' ? vehicle.model
        : spec.id === 'year' ? vehicle.year
        : spec.id === 'vin' ? knownColumnVin ?? undefined
        : undefined;
      const raw = overlayValue(spec.overlayKey, fallback);
      current[spec.overlayKey] = spec.id === 'vin'
        ? (nonBlankVin(raw) ?? knownColumnVin ?? raw)
        : raw;
    }
    return current;
  };

  const persistOverlay = async (
    nextOverrides: Record<string, unknown> | null,
    columns?: { make?: string; model?: string; year?: number; vin?: string },
  ) => {
    const dataPayload: {
      manualOverrides: Record<string, unknown> | null;
      make?: string;
      model?: string;
      year?: number;
      vin?: string;
    } = { manualOverrides: nextOverrides };
    if (columns?.make !== undefined) dataPayload.make = columns.make;
    if (columns?.model !== undefined) dataPayload.model = columns.model;
    if (columns?.year !== undefined) dataPayload.year = columns.year;
    if (columns?.vin !== undefined) dataPayload.vin = columns.vin;
    await updateMutation.mutateAsync({
      id: vehicle.id,
      data: dataPayload,
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.vehicles.detail(vehicle.id),
    });
  };

  const handleOverlaySave = async (overlayKey: string, parsed: unknown) => {
    const registry = traficomRecord[overlayKey];
    const next: Record<string, unknown> = { ...(overrides ?? {}) };
    const registryMatches =
      (parsed == null || parsed === '') && (registry == null || registry === '')
      || String(parsed) === String(registry);
    if (registryMatches) {
      delete next[overlayKey];
    } else {
      next[overlayKey] = parsed;
    }
    const columns: { make?: string; model?: string; year?: number; vin?: string } = {};
    if (overlayKey === 'make' && typeof parsed === 'string') columns.make = parsed;
    if (overlayKey === 'model' && typeof parsed === 'string') columns.model = parsed;
    if (overlayKey === 'modelYear' && typeof parsed === 'number') columns.year = parsed;
    if (overlayKey === 'vin') {
      const vin = nonBlankVin(parsed);
      if (vin !== null) columns.vin = vin;
    }
    try {
      await persistOverlay(Object.keys(next).length === 0 ? null : next, columns);
    } catch (err) {
      notifyVehicleSaveError(err, notify, t, tc);
      throw err;
    }
  };

  const handleOverlayRestore = async (overlayKey: string) => {
    const next: Record<string, unknown> = { ...(overrides ?? {}) };
    delete next[overlayKey];
    const registry = traficomRecord[overlayKey];
    const columns: { make?: string; model?: string; year?: number; vin?: string } = {};
    if (overlayKey === 'make' && typeof registry === 'string') columns.make = registry;
    if (overlayKey === 'model' && typeof registry === 'string') columns.model = registry;
    if (overlayKey === 'modelYear' && typeof registry === 'number') columns.year = registry;
    if (overlayKey === 'vin') {
      const vin = nonBlankVin(registry);
      if (vin !== null) columns.vin = vin;
    }
    try {
      await persistOverlay(Object.keys(next).length === 0 ? null : next, columns);
      notify.success(t('form.updated'), t('source.restoredDesc'));
    } catch (err) {
      notifyVehicleSaveError(err, notify, t, tc);
    }
  };

  const renderOverlayField = (id: VehicleLookupFieldId, formatDisplay?: (value: unknown) => string) => {
    const spec = lookupFieldById(id);
    if (!spec) return null;
    const fallback =
      id === 'make' ? vehicle.make
      : id === 'model' ? vehicle.model
      : id === 'year' ? vehicle.year
      : id === 'vin' ? vehicle.vin
      : undefined;
    const value = overlayValue(spec.overlayKey, fallback);
    return (
      <OverlayField
        spec={spec}
        label={t(spec.labelKey)}
        value={value}
        canUpdate={canUpdate}
        isOverride={overlayHasKey(overrides, spec.overlayKey)}
        onSave={(parsed) => handleOverlaySave(spec.overlayKey, parsed)}
        onRestore={() => { void handleOverlayRestore(spec.overlayKey); }}
        {...(formatDisplay ? { formatDisplay } : {})}
      />
    );
  };

  const handleRefresh = async () => {
    const plate = formatFinnishPlate(vehicle.registrationNumber);
    if (plate === null) return;
    setTraficomRefreshing(true);
    try {
      const result = await lookupMutation.mutateAsync({
        registrationNumber: plate,
        force: true,
        ...(knownColumnVin ? { vin: knownColumnVin } : {}),
      });
      const items = buildVehicleLookupDiff({
        lookup: result,
        currentByOverlayKey: currentDisplayByOverlayKey(),
        overrideKeys: overrides ? Object.keys(overrides) : [],
      });
      if (items.length === 0) {
        notify.info(t('lookup.noChanges'), t('lookup.noChangesDesc'));
        return;
      }
      setLookupDiff({ items, result });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        notify.warning(t('lookup.notFound'), t('lookup.notFoundDesc'));
      } else if (error instanceof ApiError && error.status === 503 && !isTemporarilyUnavailable(error)) {
        notify.warning(t('lookup.notConfigured'), t('lookup.notConfiguredDesc'));
      } else if (isTemporarilyUnavailable(error)) {
        notify.warning(tc('error'), tc('errors.temporarilyUnavailable'));
      } else {
        notify.error(tc('error'), t('lookup.failedDesc'));
      }
    } finally {
      setTraficomRefreshing(false);
    }
  };

  const handleApplyLookup = (selectedIds: VehicleLookupFieldId[]) => {
    const pending = lookupDiff;
    if (!pending || updateMutation.isPending) return;
    const applied = applyLookupToOverlay({
      lookup: pending.result,
      selectedIds,
      currentOverrides: overrides,
      currentDisplay: currentDisplayByOverlayKey(),
      existingTraficom: traficomRecord,
      registrationNumber: vehicle.registrationNumber,
    });
    const patch: {
      traficomData: Record<string, unknown>;
      manualOverrides: Record<string, unknown> | null;
      make?: string;
      model?: string;
      year?: number;
      vin?: string;
    } = {
      traficomData: applied.traficomData,
      manualOverrides: applied.manualOverrides,
    };
    if (applied.columns.make !== undefined) patch.make = applied.columns.make;
    if (applied.columns.model !== undefined) patch.model = applied.columns.model;
    if (applied.columns.year !== undefined) patch.year = applied.columns.year;
    const refreshedVin = nonBlankVin(applied.columns.vin);
    if (refreshedVin !== null) patch.vin = refreshedVin;
    void updateMutation.mutateAsync({ id: vehicle.id, data: patch })
      .then(() => {
        setLookupDiff(null);
        void queryClient.invalidateQueries({
          queryKey: queryKeys.vehicles.detail(vehicle.id),
        });
        notify.success(t('lookup.found'), t('lookup.appliedDesc'));
      })
      .catch((err: unknown) => {
        notifyVehicleSaveError(err, notify, t, tc);
      });
  };

  const shownVin =
    nonBlankVin(overlayValue('vin', knownColumnVin ?? nonBlankVin(data?.vin)))
    ?? knownColumnVin
    ?? nonBlankVin(data?.vin);

  const motStatus = getExpiryStatus(vehicle.motExpiryDate);
  const insuranceStatus = getExpiryStatus(vehicle.insuranceExpiryDate);
  const inspectionStatus = getInspectionStatus();

  const engineFuel = overlayValue('fuelType', vehicle.engineType ?? data?.fuelType);
  const engineType = typeof engineFuel === 'string' ? engineFuel : undefined;
  const enginePowerKw = overlayValue('enginePowerKw', data?.enginePowerKw);

  return (
    <>
    <ResponsiveDialog
      open={open}
      onClose={(event) => {
        (event as { stopPropagation?: () => void }).stopPropagation?.();
        onClose();
      }}
      maxWidth="md"
      fullWidth
      mobileTitle={`${vehicle.registrationNumber} - ${vehicle.make} ${vehicle.model}`}
    >
      <DialogHeader onClose={onClose}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <VehicleMakeLogo make={vehicle.make} size={48} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RegistrationPlate registrationNumber={vehicle.registrationNumber ?? '-'} />
              {liveNotes && (
                <Chip
                  icon={<WarningIcon />}
                  label={t('hasNotes')}
                  size="small"
                  color="warning"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {vehicle.make} {vehicle.model} ({vehicle.year})
            </Typography>
          </Box>
        </Box>
      </DialogHeader>
      <Divider />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={(_, v: number) => { setActiveTab(v); }}>
          <Tab icon={<AssignmentIcon />} label={t('details')} iconPosition="start" />
          <Tab icon={<PeopleIcon />} label={t('parties')} iconPosition="start" />
          <Tab icon={<HistoryIcon />} label={t('serviceHistory')} iconPosition="start" />
          <Tab icon={<AttachFileIcon />} label={t('attachments')} iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent>
        {/* Tab 0: Tiedot — Vehicle details + Traficom data merged */}
        {activeTab === 0 && (
          <Box>
            {/* Header bar: last update time + refresh button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<VerifiedIcon />}
                  label="Traficom"
                  color="primary"
                  size="small"
                />
                {data?.fetchedAt && !isNaN(new Date(data.fetchedAt).getTime()) && (
                  <Typography variant="caption" color="text.secondary">
                    {t('traficom.lastUpdated')}{' '}
                    {formatDistanceToNow(new Date(data.fetchedAt), { addSuffix: true, locale: dateFnsLocale() })}
                  </Typography>
                )}
              </Box>
              {canUpdate && (
                <Tooltip title={t('refreshDataTooltip')}>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => void handleRefresh()}
                    disabled={traficomRefreshing}
                    aria-label={t('refreshData')}
                  >
                    {t('refreshData')}
                  </Button>
                </Tooltip>
              )}
            </Box>

            {/* Inspection Alert */}
            {inspectionStatus && (
              <Alert
                severity={inspectionStatus.color}
                icon={inspectionStatus.icon}
                sx={{ mb: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {inspectionStatus.label}
                  </Typography>
                  {data?.nextInspectionDue && isValid(new Date(data.nextInspectionDue)) && (
                    <Typography variant="body2">
                      - {format(new Date(data.nextInspectionDue), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    </Typography>
                  )}
                </Box>
              </Alert>
            )}

            {/* No traficom data hint */}
            {!data && (
              <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                {t('noTraficomData')}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <VehicleTechnicalDataPanel
                  vehicleId={vehicle.id}
                  make={vehicle.make}
                  model={vehicle.model}
                  hasVin={shownVin !== null}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <VehicleServiceBookLinks
                  make={String(overlayValue('make', vehicle.make) ?? '')}
                  year={
                    typeof overlayValue('modelYear', vehicle.year) === 'number'
                      ? (overlayValue('modelYear', vehicle.year) as number)
                      : vehicle.year
                  }
                />
              </Grid>

              {/* Ajoneuvon tiedot */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionHeader
                    icon={<DirectionsCarIcon color="action" fontSize="small" />}
                    title={t('vehicleInfo')}
                  />
                  <InlineVehicleFields
                    vehicle={{
                      id: vehicle.id,
                      registrationNumber: vehicle.registrationNumber,
                      make: String(overlayValue('make', vehicle.make) ?? ''),
                      model: String(overlayValue('model', vehicle.model) ?? ''),
                      year: typeof overlayValue('modelYear', vehicle.year) === 'number'
                        ? (overlayValue('modelYear', vehicle.year) as number)
                        : overlayHasKey(overrides, 'modelYear') ? 0 : vehicle.year,
                      vin: shownVin,
                      notes: liveNotes,
                    }}
                    readOnly={!canUpdate}
                    overlay={{ traficom: traficomRecord, overrides }}
                  />
                  {shownVin == null && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t('vinMissing')}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  {renderOverlayField('color')}
                  {renderOverlayField('vehicleClass')}
                  {renderOverlayField('vehicleGroup')}
                  {renderOverlayField('bodyType')}
                  {renderOverlayField('doors')}
                  {renderOverlayField('seats')}
                </Paper>
              </Grid>

              {/* Moottori & suorituskyky */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionHeader
                    icon={<SpeedIcon color="action" fontSize="small" />}
                    title={t('traficom.enginePerformance')}
                  />
                  <DataRow label={t('engineType')} value={engineType ? t(engineTypeKeys[engineType] ?? engineType) : undefined} />
                  {renderOverlayField('engineDisplacementCc')}
                  {renderOverlayField('enginePowerKw')}
                  {typeof enginePowerKw === 'number' && (
                    <DataRow label={t('traficom.enginePowerHp')} value={Math.round(enginePowerKw * 1.36)} unit="hv" />
                  )}
                  {renderOverlayField('transmission')}
                  {renderOverlayField('driveType')}
                </Paper>
              </Grid>

              {/* Polttoaine & päästöt */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionHeader
                    icon={<LocalGasStationIcon color="action" fontSize="small" />}
                    title={t('traficom.fuelEmissions')}
                  />
                  {renderOverlayField('fuelType')}
                  <DataRow
                    label={t('traficom.fuelConsumption')}
                    value={data?.combinedFuelConsumptionL100km}
                    unit="l/100km"
                  />
                  <Divider sx={{ my: 1 }} />
                  {renderOverlayField('co2EmissionsGkm')}
                  {renderOverlayField('euroEmissionClass')}
                </Paper>
              </Grid>

              {/* Mitat & paino */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionHeader
                    icon={<StraightenIcon color="action" fontSize="small" />}
                    title={t('traficom.dimensions')}
                  />
                  {renderOverlayField('lengthMm')}
                  {renderOverlayField('widthMm')}
                  {renderOverlayField('heightMm')}
                  <Divider sx={{ my: 1 }} />
                  {renderOverlayField('curbWeightKg')}
                  {renderOverlayField('grossWeightKg')}
                </Paper>
              </Grid>

              {/* Rekisteröintitiedot */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionHeader
                    icon={<EventIcon color="action" fontSize="small" />}
                    title={t('traficom.registration')}
                  />
                  {renderOverlayField('firstRegistrationDate', (value) => {
                    if (typeof value !== 'string' || !isValid(new Date(value))) return '';
                    return format(new Date(value), 'd.M.yyyy', { locale: dateFnsLocale() });
                  })}
                  {data?.firstRegistrationDateFinland && data.firstRegistrationDateFinland !== data.firstRegistrationDate && isValid(new Date(data.firstRegistrationDateFinland)) && (
                    <DataRow
                      label={t('traficom.firstRegistrationFinland')}
                      value={format(new Date(data.firstRegistrationDateFinland), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    />
                  )}
                  <DataRow label={t('traficom.registrationStatus')} value={data?.registrationStatus} />
                  <DataRow label={t('traficom.ownershipType')} value={data?.ownershipType} />
                  <Divider sx={{ my: 1 }} />
                  {data?.vehicleTaxEurPerYear && (
                    <DataRow label={t('traficom.vehicleTax')} value={data.vehicleTaxEurPerYear} unit="€/v" />
                  )}
                </Paper>
              </Grid>

              {/* Katsastustiedot */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionHeader
                    icon={<VerifiedIcon color="action" fontSize="small" />}
                    title={t('traficom.inspection')}
                  />
                  <DataRow
                    label={t('traficom.lastInspection')}
                    value={data?.lastInspectionDate && isValid(new Date(data.lastInspectionDate)) ? format(new Date(data.lastInspectionDate), 'd.M.yyyy', { locale: dateFnsLocale() }) : undefined}
                  />
                  <DataRow
                    label={t('traficom.nextInspection')}
                    value={data?.nextInspectionDue && isValid(new Date(data.nextInspectionDue)) ? format(new Date(data.nextInspectionDue), 'd.M.yyyy', { locale: dateFnsLocale() }) : undefined}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('traficom.result')}
                    </Typography>
                    {getInspectionResultChip() ?? <Typography variant="body2">-</Typography>}
                  </Box>
                  {data?.inspectionNotes && data.inspectionNotes.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('traficom.inspectionNotes')}:
                      </Typography>
                      {data.inspectionNotes.map((note, idx) => (
                        <Typography key={idx} variant="body2" sx={{ pl: 1, mb: 0.5 }}>
                          • {note}
                        </Typography>
                      ))}
                    </>
                  )}
                </Paper>
              </Grid>

              {/* Renkaat */}
              {(data?.tireSizeFront ?? data?.tireSizeRear ?? data?.rimSize) && (
                <Grid size={{ xs: 12 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <SectionHeader
                      icon={<DirectionsCarIcon color="action" fontSize="small" />}
                      title={t('traficom.tires')}
                    />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <DataRow label={t('traficom.tireFront')} value={data?.tireSizeFront} />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <DataRow label={t('traficom.tireRear')} value={data?.tireSizeRear} />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <DataRow label={t('traficom.rimSize')} value={data?.rimSize} />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Tärkeät päivämäärät */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionHeader
                    icon={<CalendarTodayIcon color="action" fontSize="small" />}
                    title={t('importantDates')}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('motExpiry')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {motStatus && (
                        <Chip
                          label={motStatus.label}
                          size="small"
                          color={motStatus.color}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      <Typography variant="body2" fontWeight={500}>
                        {vehicle.motExpiryDate
                          ? format(new Date(vehicle.motExpiryDate), 'd.M.yyyy', { locale: dateFnsLocale() })
                          : '-'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('insuranceExpiry')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {insuranceStatus && (
                        <Chip
                          label={insuranceStatus.label}
                          size="small"
                          color={insuranceStatus.color}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      <Typography variant="body2" fontWeight={500}>
                        {vehicle.insuranceExpiryDate
                          ? format(new Date(vehicle.insuranceExpiryDate), 'd.M.yyyy', { locale: dateFnsLocale() })
                          : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Kilometrihistoria */}
              <Grid size={{ xs: 12 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <SectionHeader
                    icon={<SpeedIcon color="action" fontSize="small" />}
                    title={t('mileageHistory')}
                  />

                  {/* Viimeisin lukema */}
                  {latestMileage?.mileage != null && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('latestMileage')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h4" fontWeight={700} color="primary.main">
                          {formatNumber(latestMileage.mileage)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">km</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {latestMileage.recordedAt && format(new Date(latestMileage.recordedAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
                        {latestMileage.source && ` · ${getSourceLabel(latestMileage.source)}`}
                      </Typography>
                    </Box>
                  )}

                  {/* AreaChart */}
                  {mileageHistoryLoading ? (
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                  ) : mileageChartData.length > 1 ? (
                    <Box sx={{ height: 200 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={mileageChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="mileageGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                              <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={theme.palette.divider}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(value: number) => formatNumber(value)}
                          />
                          <RechartsTooltip
                            {...chartTooltipProps(theme)}
                            formatter={(value: number) => [`${formatNumber(value)} km`, t('mileage')]}
                            labelFormatter={(label: string) => label}
                          />
                          <Area
                            type="monotone"
                            dataKey="mileage"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            fill="url(#mileageGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : mileageChartData.length === 0 && !latestMileage?.mileage ? (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                      <SpeedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('noMileageHistory')}
                      </Typography>
                    </Box>
                  ) : null}
                </Paper>
              </Grid>

              {/* Lisätty */}
              <Grid size={{ xs: 12 }}>
                <Divider />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  {t('addedOn')} {format(new Date(vehicle.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 1: Osapuolet — Owner, Holder, Work Orderers */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            {/* Owner */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('owner')}
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => navigate(`/parties`)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.light' }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {vehicle.owner
                        ? getLinkDisplayName(vehicle.owner)
                        : vehicle.customer
                          ? `${vehicle.customer.firstName ?? ''} ${vehicle.customer.lastName ?? ''}`.trim()
                          : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {vehicle.owner?.customerPhone ?? vehicle.customer?.phone ?? ''}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Holder */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('holder')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {vehicle.holder ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.light' }}>
                      <BusinessIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {getLinkDisplayName(vehicle.holder)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {vehicle.holder.customerPhone ?? ''}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('noHolder')}
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Work Orderers */}
            {vehicle.workOrderers && vehicle.workOrderers.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('workOrderers')}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {vehicle.workOrderers.map((wo) => (
                      <Chip
                        key={wo.id}
                        icon={<GroupIcon />}
                        label={getLinkDisplayName(wo)}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Tab 2: Huoltohistoria — Service stats + Service history table */}
        {activeTab === 2 && (
          <Box>
            {/* Service Stats */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('serviceStats')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('totalServices')}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {vehicle.serviceCount}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('totalSpent')}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {formatPrice(vehicle.totalSpentCents)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('lastService')}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {vehicle.lastServiceAt
                      ? format(new Date(vehicle.lastServiceAt), 'd.M.yyyy', { locale: dateFnsLocale() })
                      : '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('nextService')}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {vehicle.nextServiceDue
                      ? format(new Date(vehicle.nextServiceDue), 'd.M.yyyy', { locale: dateFnsLocale() })
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Service History Table */}
            {serviceHistory.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">{t('noServiceHistory')}</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('date')}</TableCell>
                      <TableCell>{t('workOrder')}</TableCell>
                      <TableCell>{t('mileageAt')}</TableCell>
                      <TableCell>{t('services')}</TableCell>
                      <TableCell align="right">{t('total')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serviceHistory.map((entry) => (
                      <TableRow
                        key={entry.id}
                        hover
                        role="link"
                        tabIndex={0}
                        aria-label={t('openWorkOrder', { number: entry.workOrderNumber })}
                        data-testid="service-history-row"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openWorkOrder(entry.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openWorkOrder(entry.id);
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(entry.date), 'd.M.yyyy', { locale: dateFnsLocale() })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.workOrderNumber}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatNumber(entry.mileage)} km
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {entry.services.map((service, idx) => (
                              <Chip key={idx} label={service} size="small" />
                            ))}
                          </Box>
                          {entry.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {entry.notes}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {formatPrice(entry.totalCents)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.mechanicName}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Tab 3: Liitteet — vehicle's own files + files from its work orders (#1043) */}
        {activeTab === 3 && vehicle && (
          <Box sx={{ py: 1 }}>
            <AttachmentsCard
              entityLinks={attachmentEntityLinks}
              uploadEntityLinks={vehicleUploadLinks}
              canModify={(attachment) => !attachment.entityLinks.some((link) => link.entityType === 'booking')}
              onOpenWorkOrder={openWorkOrder}
              defaultTags={vehicle.registrationNumber ? [vehicle.registrationNumber] : []}
              cardVariant="outlined"
            />
          </Box>
        )}
      </DialogContent>

      {!hideCreateWorkOrder && (
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<BuildIcon />}
            onClick={() => {
              setCreateVehicle(toCreateWizardVehicle(vehicle, liveDetail));
              setCreateOpen(true);
              onClose();
            }}
          >
            {t('createWorkOrder')}
          </Button>
          <Box sx={{ flex: 1 }} />
        </DialogActions>
      )}

      <VehicleLookupDiffDialog
        open={lookupDiff != null}
        items={lookupDiff?.items ?? []}
        onClose={() => { setLookupDiff(null); }}
        onApply={handleApplyLookup}
      />
    </ResponsiveDialog>
    {createOpen ? createWizard : null}
    </>
  );
}
